
import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { getPhotos, getPhotoUrl } from "@/services/photoService";
import { useReveal } from "@/hooks/useReveal";
import { ArrowRight } from "lucide-react";
import type { Photo } from "@/types/supabase";

const PREFERRED_CATEGORIES = [
  "extérieur", "piscine", "terrasse", "intérieur", "salon",
  "chambre", "cuisine", "salle de bain", "environnement",
];

function selectPhotos(all: Photo[], count: number): Photo[] {
  if (all.length === 0) return [];
  const featured = all.filter((p) => p.featured);
  if (featured.length >= count) return featured.slice(0, count);

  const usedIds = new Set(featured.map((p) => p.id));
  const selected = [...featured];
  const rest = all.filter((p) => !p.featured);

  for (const cat of PREFERRED_CATEGORIES) {
    if (selected.length >= count) break;
    const match = rest.find((p) => p.category === cat && !usedIds.has(p.id));
    if (match) { selected.push(match); usedIds.add(match.id); }
  }
  for (const p of rest) {
    if (selected.length >= count) break;
    if (!usedIds.has(p.id)) { selected.push(p); usedIds.add(p.id); }
  }
  return selected.slice(0, count);
}

const GALLERY_H = 560;
const ROW_H = (GALLERY_H - 12) / 2; // 12px = gap-3

const FeaturedPhotos = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  const headerReveal = useReveal<HTMLDivElement>();

  // Separate parallax refs — not useReveal (avoid opacity:0 conflict)
  const sectionRef = useRef<HTMLElement>(null);
  const mainImgRef = useRef<HTMLImageElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    getPhotos()
      .then((all) => setPhotos(selectPhotos(all, 5)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updateParallax = useCallback(() => {
    const section = sectionRef.current;
    const img = mainImgRef.current;
    if (!section || !img) return;

    const rect = section.getBoundingClientRect();
    const viewH = window.innerHeight;
    const centre = rect.top + rect.height / 2 - viewH / 2;
    const translateY = centre * 0.2;
    const progress = Math.abs(centre) / (viewH * 0.7);
    const scale = 1.08 + Math.min(progress, 1) * 0.05;
    img.style.transform = `translateY(${translateY}px) scale(${scale})`;
  }, []);

  useEffect(() => {
    if (photos.length === 0) return;
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateParallax);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    updateParallax();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [photos.length, updateParallax]);

  if (!loading && photos.length === 0) return null;

  return (
    <section ref={sectionRef} className="bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-32">

        {/* Header */}
        <div
          ref={headerReveal.ref}
          className={`flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4 reveal ${headerReveal.revealed ? "in" : ""}`}
        >
          <div>
            <p className="eyebrow mb-3">Galerie</p>
            <h2 className="heading-lg text-villa-text">La Villa en Images</h2>
          </div>
          <Link
            to="/galerie"
            className="inline-flex items-center gap-2 text-villa-turquoise font-medium text-[15px] group flex-shrink-0"
          >
            Voir toutes les photos
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Desktop skeleton */}
        {loading && (
          <div
            className="hidden md:grid grid-cols-12 gap-3"
            style={{ height: `${GALLERY_H}px` }}
          >
            <div className="col-span-7 rounded-3xl bg-villa-offwhite animate-pulse" />
            <div className="col-span-5 flex flex-col gap-3">
              <div className="flex-1 rounded-3xl bg-villa-offwhite animate-pulse" />
              <div className="flex-1 rounded-3xl bg-villa-offwhite animate-pulse" />
            </div>
          </div>
        )}

        {/* Mobile skeleton */}
        {loading && (
          <div className="md:hidden grid grid-cols-2 gap-3">
            <div className="col-span-2 rounded-2xl bg-villa-offwhite animate-pulse" style={{ height: 240 }} />
            <div className="rounded-2xl bg-villa-offwhite animate-pulse" style={{ height: 160 }} />
            <div className="rounded-2xl bg-villa-offwhite animate-pulse" style={{ height: 160 }} />
          </div>
        )}

        {/* Gallery — renders immediately once photos arrive */}
        {!loading && photos.length > 0 && (
          <>
            {/* Desktop: asymmetric layout */}
            <div className="hidden md:grid grid-cols-12 gap-3">
              {/* Main large photo — parallax via ref */}
              {photos[0] && (
                <div
                  className="col-span-7 rounded-3xl overflow-hidden"
                  style={{ height: `${GALLERY_H}px` }}
                >
                  <img
                    ref={mainImgRef}
                    src={`${getPhotoUrl(photos[0].storage_path)}?width=1200&quality=85`}
                    alt={photos[0].title || "Villa Douce Vague"}
                    className="w-full h-full object-cover"
                    style={{ willChange: "transform", transformOrigin: "center" }}
                    loading="lazy"
                  />
                </div>
              )}

              {/* Right column — explicit height so images don't collapse */}
              <div
                className="col-span-5 flex flex-col gap-3"
                style={{ height: `${GALLERY_H}px` }}
              >
                {photos.slice(1, 3).map((photo) => (
                  <div
                    key={photo.id}
                    className="img-hover rounded-3xl overflow-hidden flex-1"
                  >
                    <img
                      src={`${getPhotoUrl(photo.storage_path)}?width=700&quality=85`}
                      alt={photo.title || "Villa Douce Vague"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {/* Bottom row */}
              {photos.length > 3 && (
                <div className="col-span-12 grid grid-cols-2 gap-3" style={{ height: 240 }}>
                  {photos.slice(3, 5).map((photo) => (
                    <div key={photo.id} className="img-hover rounded-3xl overflow-hidden">
                      <img
                        src={`${getPhotoUrl(photo.storage_path)}?width=800&quality=85`}
                        alt={photo.title || "Villa Douce Vague"}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile */}
            <div className="md:hidden grid grid-cols-2 gap-3">
              {photos.map((photo, i) => (
                <div
                  key={photo.id}
                  className={`img-hover rounded-2xl overflow-hidden ${i === 0 ? "col-span-2" : ""}`}
                  style={{ height: i === 0 ? 240 : 160 }}
                >
                  <img
                    src={`${getPhotoUrl(photo.storage_path)}?width=700&quality=85`}
                    alt={photo.title || "Villa Douce Vague"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedPhotos;
